<script setup lang="ts">
    import type { AccessGroupNode, AccessLevel, ApiPermission } from '@/js/services/api/role/types';
    import { RoleOrgAccessLevel } from '@/js/services/api/role/types';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { CirclePlusIcon, CircleMinusIcon } from '@lucide/vue';
    import EchoTooltip from '../EchoTooltip.vue';
    import SummaryOrgList from './SummaryOrgList.vue';
    import { VisiblePermissions } from '@/js/consts/permissions';
    import { computed } from 'vue';

    const props = defineProps<{
        // Module permission schema (areas + flag definitions).
        permissions: ApiPermission[];
        // Effective (role overlaid by user override) permission mask per area.
        effectivePermissions: Record<string, number>;
        // Role baseline, for the override badges.
        rolePermissions?: Record<string, number>;
        // Effective org-access tree and its role baseline (flattened by id).
        groups: AccessGroupNode[];
        baselineById?: Map<string, AccessGroupNode>;
        // Effective org access level + the role's, to flag a level override.
        accessLevel: AccessLevel;
        roleAccessLevel?: RoleOrgAccessLevel;
        userName?: string;
        roleName?: string;
    }>();

    const isCustom = computed(() => props.accessLevel === RoleOrgAccessLevel.Custom);

    const accessLevelOverridden = computed(
        () => props.roleAccessLevel !== undefined && props.accessLevel !== props.roleAccessLevel
    );

    // For Full/Agg the whole org is granted, so we summarise it as a single line
    // rather than enumerate every level; individual-data inclusion differs.
    const allLevelsNote = computed(() =>
        props.accessLevel === RoleOrgAccessLevel.Full
            ? __('user-summary-individual-included')
            : __('user-summary-individual-excluded')
    );

    // Flag list per area, mirroring ModuleAccessControl's ordering.
    const getFlags = (permission: ApiPermission) =>
        Object.entries(permission.valueDefinitions)
            .filter(([key]) => Number(key) !== 0)
            .map(([key, label]) => ({
                flag: Number(key),
                label,
                translatedLabel: __untypedExpression('permission-' + label),
            }))
            .sort((a, b) => {
                if (a.flag === 1) return -1;
                if (b.flag === 1) return 1;
                return a.translatedLabel.localeCompare(b.translatedLabel);
            });

    const hasFlag = (dict: Record<string, number> | undefined, name: string, flag: number) =>
        ((dict?.[name] ?? 0) & flag) === flag;

    type FlagState = { flag: number; label: string; diff: 'added' | 'removed' | null };

    // Show a flag when it's effectively granted, or when the role grants it but
    // the user override revokes it (so a denial-despite-the-role is still visible).
    // Without a baseline (the role admin reuses this view) there's nothing to
    // diverge from, so we simply list the granted flags with no badges.
    const areaFlags = (permission: ApiPermission): FlagState[] =>
        getFlags(permission).reduce<FlagState[]>((acc, { flag, label }) => {
            const effective = hasFlag(props.effectivePermissions, permission.name, flag);
            if (!props.rolePermissions) {
                if (effective) acc.push({ flag, label, diff: null });
                return acc;
            }
            const role = hasFlag(props.rolePermissions, permission.name, flag);
            if (!effective && !role) return acc;
            acc.push({
                flag,
                label,
                diff: effective === role ? null : effective ? 'added' : 'removed',
            });
            return acc;
        }, []);

    const moduleAreas = computed(() =>
        props.permissions
            .filter((perm) => VisiblePermissions.includes(perm.name))
            .map((perm) => ({ perm, flags: areaFlags(perm) }))
    );

    const flagTooltip = (kind: 'added' | 'removed') =>
        kind === 'added'
            ? __('user-permission-added-from-role', {
                  userName: props.userName ?? '',
                  roleName: props.roleName ?? '',
              })
            : __('user-permission-removed-from-role', {
                  userName: props.userName ?? '',
                  roleName: props.roleName ?? '',
              });
</script>

<template>
    <div class="flex flex-col gap-8">
        <!-- Data access -->
        <section class="flex flex-col gap-3">
            <h3 class="heading-sm">{{ __('role-tab-data') }}</h3>
            <div class="flex flex-col gap-1.5">
                <div class="flex items-center gap-2">
                    <h4 class="text-body-bold">{{ __('user-summary-org-levels-heading') }}</h4>
                    <span v-if="accessLevelOverridden" class="text-caption text-primary-carrot">
                        ({{ __('user-permission-overrides-role') }})
                    </span>
                </div>
                <p v-if="!isCustom" class="text-body">
                    {{ __('org-levels') }}
                    <span>({{ allLevelsNote }})</span>
                </p>
                <div v-else class="-ml-8">
                    <SummaryOrgList :nodes="groups" :baseline-by-id="baselineById" />
                </div>
            </div>
        </section>

        <!-- Functionality -->
        <section class="flex flex-col gap-4 border-t border-gray-lightest pt-6">
            <h3 class="heading-sm">{{ __('role-tab-functionality') }}</h3>
            <div v-for="{ perm, flags } in moduleAreas" :key="perm.id" class="flex flex-col gap-1">
                <h4 class="text-body-bold">
                    {{ __untypedExpression('permission-area-' + perm.name) }}
                </h4>
                <p v-if="!flags.length" class="text-body text-gray">
                    {{ __('user-summary-no-access') }}
                </p>
                <ul v-else class="flex flex-col">
                    <li
                        v-for="f in flags"
                        :key="f.flag"
                        class="flex items-center gap-2 py-0.5 text-body"
                    >
                        <span :class="f.diff === 'removed' ? 'text-gray line-through' : ''">
                            {{ __untypedExpression('permission-' + f.label) }}
                        </span>
                        <EchoTooltip
                            v-if="f.diff === 'added'"
                            :label="flagTooltip('added')"
                            class="inline-flex"
                            tabindex="0"
                        >
                            <CirclePlusIcon class="size-4 text-primary-carrot" />
                        </EchoTooltip>
                        <EchoTooltip
                            v-else-if="f.diff === 'removed'"
                            :label="flagTooltip('removed')"
                            class="inline-flex"
                            tabindex="0"
                        >
                            <CircleMinusIcon class="size-4 text-primary-carrot" />
                        </EchoTooltip>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>
